/**
 * Migration utilities for converting from lists to collections
 * Handles backward compatibility and data transformation
 */

import { convertLegacyListToCollection } from '../types/elementTypes';

const STORAGE_VERSION_KEY = 'minderbox_storage_version';
const LEGACY_STORAGE_VERSION_KEY = 'easydone_storage_version';
const CURRENT_VERSION = '2.0';
const LEGACY_VERSION = '1.0';

/**
 * Checks if migration is needed
 * @returns {boolean}
 */
export const needsMigration = () => {
    const version = localStorage.getItem(STORAGE_VERSION_KEY)
        || localStorage.getItem(LEGACY_STORAGE_VERSION_KEY);
    return !version || version === LEGACY_VERSION;
};

/**
 * Marks migration as complete
 */
export const markMigrationComplete = () => {
    localStorage.setItem(STORAGE_VERSION_KEY, CURRENT_VERSION);
};

/**
 * Migrates legacy folders with lists to folders with collections
 * @param {Array} legacyFolders - Array of legacy folders with lists
 * @returns {Array} Migrated folders with collections
 */
export const migrateFoldersToCollections = (legacyFolders) => {
    if (!legacyFolders || !Array.isArray(legacyFolders)) {
        return [];
    }

    return legacyFolders.map(folder => ({
        ...folder,
        collections: (folder.lists || []).map(list => 
            convertLegacyListToCollection(list)
        ),
        // Remove the old lists property after migration
        lists: undefined,
    }));
};

/**
 * Migrates selectedListId to selectedCollectionId
 * @returns {string|null} The migrated collection ID or null
 */
export const migrateSelectedId = () => {
    const legacySelectedId = localStorage.getItem('selectedListId');
    if (legacySelectedId) {
        try {
            const parsedId = JSON.parse(legacySelectedId);
            // Move to new storage key
            localStorage.setItem('selectedCollectionId', JSON.stringify(parsedId));
            // Optionally remove old key
            // localStorage.removeItem('selectedListId');
            return parsedId;
        } catch (e) {
            console.error('Failed to migrate selected ID:', e);
            return null;
        }
    }
    return null;
};

/**
 * Performs full migration of all stored data
 * @returns {Object} Migrated data { folders, selectedCollectionId }
 */
export const performFullMigration = () => {
    console.log('Starting migration from lists to collections...');
    
    // Get legacy data
    const savedFolders = localStorage.getItem('folders');
    const savedLists = localStorage.getItem('lists');
    
    let folders = [];
    
    // Parse folders
    if (savedFolders) {
        try {
            folders = JSON.parse(savedFolders);
        } catch (e) {
            console.error('Failed to parse folders:', e);
        }
    }
    
    // Handle unfiled lists (legacy format)
    let unfiledLists = [];
    if (savedLists) {
        try {
            unfiledLists = JSON.parse(savedLists);
        } catch (e) {
            console.error('Failed to parse lists:', e);
        }
    }
    
    // Migrate unfiled lists to General folder
    if (unfiledLists.length > 0) {
        const generalFolder = folders.find(f => f.name === 'General');
        if (generalFolder) {
            generalFolder.lists = [...(generalFolder.lists || []), ...unfiledLists];
        } else {
            folders.push({
                id: Date.now().toString(),
                name: 'General',
                lists: unfiledLists,
            });
        }
        // Remove legacy lists storage
        localStorage.removeItem('lists');
    }
    
    // Migrate folders to collections
    const migratedFolders = migrateFoldersToCollections(folders);
    
    // Migrate selected ID
    const selectedCollectionId = migrateSelectedId();
    
    // Save migrated data
    localStorage.setItem('folders', JSON.stringify(migratedFolders));
    
    // Mark migration as complete
    markMigrationComplete();
    
    console.log('Migration complete!');
    
    return {
        folders: migratedFolders,
        selectedCollectionId,
    };
};

/**
 * Validates that data is in the new collection format
 * @param {Array} folders - Folders to validate
 * @returns {boolean}
 */
export const validateCollectionFormat = (folders) => {
    if (!folders || !Array.isArray(folders)) {
        return false;
    }
    
    // Check if any folder has 'lists' instead of 'collections'
    const hasLegacyLists = folders.some(folder => folder.lists && !folder.collections);
    if (hasLegacyLists) {
        return false;
    }
    
    // Check if collections have 'elements' instead of 'todos'
    const hasLegacyTodos = folders.some(folder => 
        folder.collections && folder.collections.some(col => col.todos && !col.elements)
    );
    if (hasLegacyTodos) {
        return false;
    }
    
    return true;
};
